import type { EntryModel, PostEntry } from './entry';
import type { Part11Signature } from './part11';
import type { FormModel } from './form';
import { isClientOnly, toWire } from './fieldAnswer';

/**
 * The save body for an entry, built from the form as the reader left it (ENG-9018 decision 8). The
 * per-type encoding is `toWire`'s; this decides which keys go. ADR 0009 records what stopped going.
 */

export interface EntryPayloadContext {
  signature?: Part11Signature;
  entryToken?: string;
  studyId?: string;
  /**
   * The loaded entry's `updatedAt`, sent as `expectedUpdatedAt` so the API's version check refuses an
   * edit against an entry someone else saved since (ENG-9073 decision 7). The re-read after each save
   * takes the new one, or a reader's second save would 409 against their own first.
   */
  version?: Date;
  /** When the save was made, in milliseconds. Passed in so the builder never reads the clock. */
  revisionDate: number;
}

/**
 * Left out of the body:
 * - the submit button and branding the entry adds under random ids, which answer nothing on the form;
 * - archived fields, which are never drawn. PATCH merges the body over the stored items, so a key left
 *   out keeps its stored value;
 * - a key `toWire` wrote as `undefined` (a single dropdown with nothing picked), which `JSON.stringify`
 *   drops anyway.
 *
 * A field a Hide effect hid is still sent with its value: Hide does not clear, and an always-hidden
 * field is where an Assign or a default puts its value. Show clears the fields it hides itself.
 *
 * `loadedEntry` is the entry the save writes over, `undefined` for a new one. Nothing in the body
 * depends on it yet: the version comes in through the context.
 */
export const buildEntryPayload = (
  form: Pick<FormModel, 'items'> | undefined,
  loadedEntry: EntryModel | undefined,
  { signature, entryToken, studyId, version, revisionDate }: EntryPayloadContext
): PostEntry & Required<Pick<PostEntry, 'items'>> => {
  const items: Record<string, string | number> = {};
  for (const field of form?.items ?? []) {
    if (isClientOnly(field) || field.archivedAt) {
      continue;
    }
    for (const [key, value] of Object.entries(toWire(field))) {
      if (value !== undefined) {
        items[key] = value;
      }
    }
  }

  const payload: PostEntry & Required<Pick<PostEntry, 'items'>> = { studyId, items, revisionDate, entryToken, signature };
  // A new entry has no version, and the key stays out rather than going as `undefined`. Neither does an `updatedAt`
  // that did not parse: `toISOString` throws on one, outside the save's `try`, which would leave the loader spinning.
  if (version && !Number.isNaN(version.getTime())) {
    payload.expectedUpdatedAt = version.toISOString();
  }
  return payload;
};
